import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Shield, Clock, MapPin, CreditCard, ChevronRight, Bus, Navigation, Ticket } from 'lucide-react';
import Footer from '@/components/Footer';
import AnimatedHero from '@/components/AnimatedHero';
import { getPopularRoutes, PopularRoute } from '@/services/routeService';
import { getNetworkStats, type NetworkStats } from '@/services/counterService';
import { getToday } from '@/lib/utils';
import HomepageNoticesSection from '@/components/notices/HomepageNoticesSection';
import GroupShowcase from '@/components/promotions/GroupShowcase';
import PageHead from '@/components/PageHead';
import { useLanguage } from '@/contexts/LanguageContext';
import { formatDisplayNumber, localizeDigits, type DisplayLanguage } from '@/lib/formatters';

const features = [
  { icon: Navigation, titleKey: 'feat_tracking', descKey: 'feat_tracking_desc' },
  { icon: Bus, titleKey: 'feat_fleet', descKey: 'feat_fleet_desc' },
  { icon: MapPin, titleKey: 'feat_seat', descKey: 'feat_seat_desc' },
  { icon: CreditCard, titleKey: 'feat_pay', descKey: 'feat_pay_desc' },
  { icon: Clock, titleKey: 'feat_ontime', descKey: 'feat_ontime_desc' },
  { icon: Shield, titleKey: 'feat_safe', descKey: 'feat_safe_desc' },
];

function localizeRouteDuration(value: string, lang: DisplayLanguage): string {
  if (lang !== 'bn') return value;
  const match = value.match(/^(\d+)h\s+(\d+)m$/);
  return match
    ? `${localizeDigits(match[1], lang)} ঘণ্টা ${localizeDigits(match[2], lang)} মিনিট`
    : localizeDigits(value, lang);
}

function localizeRouteDistance(value: string, lang: DisplayLanguage): string {
  if (lang !== 'bn') return value;
  return localizeDigits(value.replace(/\s*km$/i, ' কিমি'), lang);
}

export default function LandingPage() {
  const { lang, t } = useLanguage();
  const [routes, setRoutes] = useState<PopularRoute[]>([]);
  const [routesLoading, setRoutesLoading] = useState(true);
  const [stats, setStats] = useState<NetworkStats | null>(null);

  useEffect(() => {
    getPopularRoutes()
      .then(setRoutes)
      .finally(() => setRoutesLoading(false));
    getNetworkStats().then(setStats).catch(() => setStats(null));
  }, []);

  // Every figure here is a live count. The strip used to read
  // "94% On-Time / 24/7 / 100% / 45+ Active Routes" — the on-time rate and the
  // passenger total have no source anywhere in the system, the route count was
  // off by 9x, and the "24/7" contradicted the Support page's stated 8AM–12AM.
  // AGENTS.md forbids fabricated metrics, so nothing renders until it is real.
  const trustItems = stats
    ? [
        { value: formatDisplayNumber(stats.activeRoutes, lang), labelKey: 'home_stat_routes' },
        { value: formatDisplayNumber(stats.terminals, lang), labelKey: 'home_stat_terminals' },
        { value: formatDisplayNumber(stats.counterPoints, lang), labelKey: 'home_stat_counters' },
        { value: formatDisplayNumber(stats.districts, lang), labelKey: 'home_stat_districts' },
      ]
    : [];
  return (
    <div className="min-h-screen bg-background">
      <PageHead title="Book Intercity Bus Tickets" description="Book premium intercity bus tickets with Star Line Group — Bangladesh's trusted bus service. AC & Non-AC coaches." />
      <AnimatedHero />

      {/* Trust Strip — hidden entirely until the real counts land, rather than
          flashing placeholder numbers the operator would have to defend. */}
      {trustItems.length > 0 && (
      <section className="border-y border-border/50 bg-card/40">
        <div className="container py-6 sm:py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {trustItems.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center"
              >
                <div className="font-display text-fluid-h3 font-bold text-gradient-primary mb-1">{item.value}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{t(item.labelKey)}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Features */}
      <section className="section-spacing">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-14"
          >
            <span className="text-[10px] sm:text-xs font-semibold tracking-widest text-accent-strong uppercase mb-3 block">{t('home_why')}</span>
            <h2 className="font-display text-fluid-h2 font-bold mb-3 sm:mb-4">{t('home_why_title')}</h2>
            <p className="text-fluid-p text-muted-foreground max-w-lg mx-auto">{t('home_why_sub')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="glass-card-elevated p-5 sm:p-7 card-hover group cursor-pointer hover:border-primary/30"
              >
                <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4 sm:mb-5 group-hover:bg-primary/15 transition-colors">
                  <f.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-display font-semibold text-base sm:text-lg mb-1 sm:mb-2">{t(f.titleKey)}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{t(f.descKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Updates & Notices */}
      <HomepageNoticesSection />

      {/* Popular Routes */}
      <section className="section-spacing bg-card/20">
        <div className="container">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-3 mb-8 sm:mb-10">
            <div>
              <span className="text-[10px] sm:text-xs font-semibold tracking-widest text-accent-strong uppercase mb-2 sm:mb-3 block">{t('home_popular')}</span>
              <h2 className="font-display text-fluid-h3 font-bold">{t('home_top_routes')}</h2>
            </div>
            <Link to="/routes" className="text-xs sm:text-sm font-medium text-primary hover:text-primary/80 flex items-center gap-1 transition-colors min-h-[44px] sm:min-h-0 py-2 sm:py-0">
              {t('home_all_routes')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {routesLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="glass-card p-5 animate-pulse">
                  <div className="h-5 bg-secondary rounded w-3/4 mb-3" />
                  <div className="flex justify-between">
                    <div className="h-4 bg-secondary rounded w-1/3" />
                    <div className="h-4 bg-secondary rounded w-1/4" />
                  </div>
                </div>
              ))
            ) : routes.slice(0, 8).map((route, i) => (
              <motion.div
                key={route.id}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                <Link
                  to={`/search?from=${encodeURIComponent(route.from)}&to=${encodeURIComponent(route.to)}&date=${getToday()}&passengers=1`}
                  className="glass-card p-5 block card-hover group cursor-pointer"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-display font-semibold">{route.from}</span>
                    <ChevronRight className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="font-display font-semibold">{route.to}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{localizeRouteDuration(route.duration, lang)}{route.distance ? ` • ${localizeRouteDistance(route.distance, lang)}` : ''}</span>
                    <span className="text-accent-strong font-bold">৳{localizeDigits(route.basePrice, lang)}+</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Star Line Group showcase — deliberately after the booking path, and
          self-hiding when nothing is scheduled. */}
      <GroupShowcase />

      {/* CTA */}
      <section className="section-spacing">
        <div className="container">
          <div className="glass-card-elevated p-8 sm:p-12 md:p-20 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
            <div className="relative z-10">
              <span className="text-xs font-semibold tracking-widest text-accent-strong uppercase mb-4 block">{t('home_ready')}</span>
              <h2 className="font-display text-3xl md:text-5xl font-bold mb-5 tracking-tight">{t('home_book_next')}</h2>
              <p className="text-muted-foreground mb-10 max-w-md mx-auto">{t('home_book_next_sub')}</p>
              <Link
                to="/search"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-9 py-4 rounded-xl font-semibold text-sm hover:bg-primary/90 transition-all btn-primary-glow"
              >
                <Ticket className="w-4 h-4" />
                {t('nav_search')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
